// resolveBrandAndVariant — UPC / OCR / OFF productName segmentation.
//
// Inputs: raw product text + a known canonical + the canonical's
// `info.brands.common` array (Haiku-populated brand catalog with
// optional `variants`).
//
// Output: { brand, variant, claims, leftover, residual }
//   brand    — the matched parent brand (e.g. "Pepsi")
//   variant  — the matched variant qualifier (e.g. "Wild Cherry")
//                or null if no variant matched
//   claims   — leftover descriptive tokens that aren't brand or
//                variant (e.g. ["Premium"]) — the existing claims
//                axis demotes adjectives, marketing copy, sizes, etc.
//   leftover — joined claim tokens, mostly for debugging
//   residual — the original text with brand+variant stripped
//
// Why this exists:
//   OFF productName for "Pepsi Wild Cherry 12 fl oz" or
//   "Fitz's Premium Root Beer 12 oz Glass Bottle" mixes brand,
//   variant, marketing adjectives, and packaging into one string.
//   Without segmentation, every value above gets jammed into either
//   `brand` or the synthetic-canonical name, polluting both axes.
//
//   This resolver runs AFTER canonical resolution. The canonical has
//   already been pulled (via inferCanonicalFromName /
//   resolveCanonicalWithClaims) — what's left is the brand + variant
//   + adjective separation.
//
// Algorithm:
//   1. Lowercase + tokenize.
//   2. For each known brand of the canonical, sorted by name length
//      DESC (longest match wins — "Vienna Beef" beats "Vienna"):
//        a. If the brand name appears in the text, mark matched.
//        b. Strip brand tokens from the text.
//   3. For each variant of the matched brand (sorted by length DESC):
//        a. If the variant name (or its `fullName`) appears in the
//           remaining text, mark matched.
//        b. Strip variant tokens.
//   4. Anything left over that isn't a stop-word, package size,
//      number, or unit-of-measure → claims.

// Exported for segmentScanText (the scan-text choke point) so the
// phrase-fallback variant pass filters packaging/size noise with the
// SAME vocabulary as the catalog segmenter — one list, no drift.
export const PRODUCT_STOP_WORDS = new Set([
  "the", "a", "an", "of", "from", "by", "with", "for", "to", "in", "on",
  "and", "&", "or", "+", "-", "—", "·", "/", ",", ".",
  // Packaging / size noise commonly stamped on product names
  "pack", "case", "bottle", "bottles", "can", "cans", "carton", "cartons",
  "box", "boxes", "jar", "jars", "tub", "tubs", "tin", "tins", "pouch",
  "pouches", "container", "package", "packages", "loaf", "loaves",
  "single", "singles", "double", "triple", "value", "family", "size",
  "ct", "count",
  // Units (those handled by parsePackageSize, but appear in raw text)
  "oz", "fl", "lb", "lbs", "g", "kg", "ml", "l", "qt", "quart", "quarts",
  "gallon", "gallons", "pint", "pints", "cup", "cups",
]);

const ADJECTIVE_WHITELIST = new Set([
  // Common marketing adjectives that the user explicitly called out as
  // claims — they're meaningful enough to keep but never standalone
  // brand/variant matches.
  "premium", "classic", "original", "authentic", "select", "reserve",
  "deluxe", "signature", "artisanal", "artisan", "gourmet", "natural",
  "organic", "imported", "domestic", "small_batch", "handcrafted",
  "traditional", "heritage", "specialty",
]);

// Exported alongside PRODUCT_STOP_WORDS for segmentScanText — both
// passes must tokenize identically or phrase boundaries drift.
export function tokenizeProductText(text) {
  if (!text || typeof text !== "string") return [];
  return text
    .toLowerCase()
    .replace(/[^a-z0-9'&\-\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}
const tokenize = tokenizeProductText;

// Find the first run of `needleTokens` in `haystackTokens`. Returns
// the [start, end) index or null. Case-insensitive (both arrays
// already lowercased).
function findRun(haystack, needle) {
  if (needle.length === 0) return null;
  outer: for (let i = 0; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return [i, i + needle.length];
  }
  return null;
}

// Strip the [start, end) token range from haystack; returns the
// reduced array (does not mutate).
function stripRun(haystack, range) {
  if (!range) return haystack;
  return [...haystack.slice(0, range[0]), ...haystack.slice(range[1])];
}

/**
 * @param {string} text — raw product text (productName, OCR result)
 * @param {object[]} brands — output of brandsForCanonical(canonicalId, ...)
 *                             — items carry { display, variants? }
 * @returns {{ brand: string|null, variant: string|null,
 *             variantFullName: string|null, claims: string[],
 *             residual: string }}
 */
export function resolveBrandAndVariant(text, brands) {
  if (!text || !Array.isArray(brands) || brands.length === 0) {
    return { brand: null, variant: null, variantFullName: null,
             claims: [], residual: text || "" };
  }

  let tokens = tokenize(text);
  let brand = null;
  let brandTokens = null;
  let matchedBrandRecord = null;

  // Sort brands by name length DESC so "Vienna Beef" matches before
  // "Vienna", "Coca-Cola" before "Coca", etc.
  const sortedBrands = [...brands].sort((a, b) =>
    (b.display || "").length - (a.display || "").length
  );

  for (const b of sortedBrands) {
    const bTokens = tokenize(b.display);
    const hit = findRun(tokens, bTokens);
    if (hit) {
      brand = b.display;
      brandTokens = bTokens;
      matchedBrandRecord = b;
      tokens = stripRun(tokens, hit);
      break;
    }
  }

  let variant = null;
  let variantFullName = null;
  if (matchedBrandRecord && Array.isArray(matchedBrandRecord.variants)) {
    // Sort variants longest first too — multi-word variants ("Sour
    // Cream & Onion", "Wild Cherry") need to win over substring
    // single-word ones.
    const sortedVariants = [...matchedBrandRecord.variants].sort((a, b) => {
      const al = ((a.fullName || a.name || "")).length;
      const bl = ((b.fullName || b.name || "")).length;
      return bl - al;
    });
    for (const v of sortedVariants) {
      // Try fullName first ("Pepsi Wild Cherry" — full product name as
      // printed), then bare variant ("Wild Cherry"). The fullName might
      // include the brand again so we have to match against the
      // ORIGINAL tokens (pre-strip) for that lookup.
      const tryStrings = [];
      if (typeof v.fullName === "string" && v.fullName.trim()) {
        tryStrings.push(v.fullName);
      }
      if (typeof v.name === "string" && v.name.trim()) {
        tryStrings.push(v.name);
      }
      let matched = false;
      for (const s of tryStrings) {
        const vTokens = tokenize(s);
        const hit = findRun(tokens, vTokens);
        if (hit) {
          variant = v.name || s;
          variantFullName = v.fullName || `${brand} ${variant}`.trim();
          tokens = stripRun(tokens, hit);
          matched = true;
          break;
        }
      }
      if (matched) break;
    }
  }

  // Anything left → claims. Filter out stop-words, pure-numeric tokens,
  // and unit suffixes. Adjectives in the whitelist pass through;
  // anything else also passes through as a claim (better to over-claim
  // than under — claims are easy to dismiss in UI, missing brand/
  // variant data is invisible).
  const claims = [];
  for (const t of tokens) {
    if (!t) continue;
    if (PRODUCT_STOP_WORDS.has(t)) continue;
    if (/^\d+(\.\d+)?$/.test(t)) continue; // pure number
    if (/^\d/.test(t)) continue;            // starts with digit (e.g. "12oz")
    claims.push(t);
  }
  // Title-case claims for display + dedupe case-insensitively.
  const seen = new Set();
  const titledClaims = [];
  for (const c of claims) {
    const key = c.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    titledClaims.push(
      c.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    );
  }

  return {
    brand,
    variant,
    variantFullName,
    claims: titledClaims,
    residual: tokens.join(" "),
  };
}
